"use client";

import type { ReactNode } from "react";
import { seriesPoints, smoothPath, type Point } from "./charts.ts";
import { useAnimatedNumber, useInView } from "./hooks.ts";

/* ──────────────────────────────────────────────────────────────────────────
   One headline figure.

   The discipline this exists to enforce: a dashboard covered in twenty cards
   in twenty colours has no hierarchy, and nothing on it stands out. Four to
   six of these, read first, then the detail underneath.

   The figure counts up rather than appearing, so changing the period reads as
   the same number moving instead of a different number arriving. It counts
   only once, when the card is first scrolled to.
   ────────────────────────────────────────────────────────────────────────── */

export function Stat({
  label,
  icon,
  value,
  previous,
  format = "number",
  note,
  colour,
  spark,
  href,
  tone,
}: {
  label: string;
  icon?: ReactNode;
  value: number;
  /** The same figure last period. Omit when there is nothing to compare to. */
  previous?: number | null;
  format?: "number" | "percent";
  note?: string;
  colour?: string;
  /** A trend behind the figure. Nulls are holes, not zeros. */
  spark?: (number | null)[];
  href?: string;
  /** Overrides the colour of the delta, for a figure where up is bad. */
  tone?: "good" | "bad" | "flat";
}) {
  const [ref, seen] = useInView<HTMLDivElement>();
  const animated = useAnimatedNumber(seen ? value : 0);

  const shown =
    format === "percent"
      ? `${Math.round(animated)}%`
      : Math.round(animated).toLocaleString();

  // Null rather than zero when there is no earlier period: "no change" and
  // "nothing to compare to" are different claims and only one is honest.
  const delta =
    previous === null || previous === undefined || previous === 0
      ? null
      : Math.round(((value - previous) / previous) * 100);

  const direction = delta === null ? "flat" : delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const chipTone = tone ?? (direction === "up" ? "good" : direction === "down" ? "bad" : "flat");

  const points = spark?.length
    ? (seriesPoints({ values: spark, width: 108, height: 30, padding: 3 }).filter(Boolean) as Point[])
    : [];

  const body = (
    <>
      <span className="stat-label">
        {icon}
        {label}
      </span>
      <span className="stat-figure">
        <span className="stat-value">{shown}</span>
        {delta !== null ? (
          <span className={`stat-delta ${chipTone}`}>
            {direction === "up" ? "▲" : direction === "down" ? "▼" : "•"} {Math.abs(delta)}%
          </span>
        ) : null}
      </span>
      {points.length > 1 ? (
        <svg className="stat-spark" viewBox="0 0 108 30" aria-hidden="true" preserveAspectRatio="none">
          <path d={`${smoothPath(points)} L ${points[points.length - 1].x} 30 L ${points[0].x} 30 Z`} className="fill" />
          <path d={smoothPath(points)} className="line" />
        </svg>
      ) : null}
      {note ? <span className="stat-note">{note}</span> : null}
    </>
  );

  const style = { ["--stat-colour" as string]: colour ?? "var(--accent)" };

  return href ? (
    <a className="stat linked" href={href} style={style} ref={ref as unknown as (node: HTMLAnchorElement | null) => void}>
      {body}
    </a>
  ) : (
    <div className="stat" style={style} ref={ref}>
      {body}
    </div>
  );
}

/**
 * A bare trend line, sized for a table row.
 *
 * No figure, no label, no axis. At forty pixels the only thing legible is the
 * shape, and anything drawn beside it competes with the row it belongs to.
 * The last point is marked because "where it is now" is the one value anybody
 * reads off a line this small.
 *
 * A run of zeros draws as a flat baseline rather than as nothing, so an idle
 * link and a link with no data are visibly different states.
 */
export function Spark({
  values,
  width = 64,
  height = 26,
  label,
}: {
  values: number[];
  width?: number;
  height?: number;
  /** Read out instead of the drawing, which is meaningless to a screen reader. */
  label: string;
}) {
  const total = values.reduce((sum, value) => sum + value, 0);

  if (values.length < 2 || total === 0) {
    return (
      <span className="row-spark empty" role="img" aria-label={label}>
        <svg viewBox={`0 0 ${width} ${height}`} aria-hidden="true" preserveAspectRatio="none">
          <line x1="2" y1={height - 3} x2={width - 2} y2={height - 3} className="flat" />
        </svg>
      </span>
    );
  }

  const points = seriesPoints({ values, width, height, padding: 3 }).filter(Boolean) as Point[];
  const line = smoothPath(points);
  const last = points[points.length - 1];

  return (
    <span className="row-spark" role="img" aria-label={label}>
      <svg viewBox={`0 0 ${width} ${height}`} aria-hidden="true" preserveAspectRatio="none">
        <path d={`${line} L ${last.x} ${height} L ${points[0].x} ${height} Z`} className="fill" />
        <path d={line} className="line" />
        <circle cx={last.x} cy={last.y} r="2" className="tip" />
      </svg>
      <b>{total.toLocaleString()}</b>
    </span>
  );
}

/**
 * A ranked list with a bar behind each row.
 *
 * The bar is proportional to the leader rather than to the total: the question
 * a ranking answers is "how far ahead is the top one", and against a total the
 * whole list collapses into slivers as soon as there are more than a few rows.
 */
export function Ranking({
  rows,
  empty,
}: {
  rows: { id: string; label: string; note?: string; value: number; href?: string; image?: string | null }[];
  empty: ReactNode;
}) {
  if (rows.length === 0) return <>{empty}</>;
  const peak = Math.max(...rows.map((row) => row.value), 1);

  return (
    <div className="ranking">
      {rows.map((row, index) => (
        <a className="ranking-row" key={row.id} href={row.href} style={{ animationDelay: `${index * 40}ms` }}>
          <span className="ranking-rank">{index + 1}</span>
          {row.image !== undefined ? (
            <span className="ranking-face">{row.image ? <img src={row.image} alt="" /> : null}</span>
          ) : null}
          <span className="ranking-name">
            <strong className="truncate">{row.label}</strong>
            {row.note ? <small className="truncate">{row.note}</small> : null}
          </span>
          <span className="ranking-bar">
            <i style={{ width: `${Math.max(3, (row.value / peak) * 100)}%` }} />
          </span>
          <b className="ranking-value">{row.value.toLocaleString()}</b>
        </a>
      ))}
    </div>
  );
}
